import React, { useCallback, useState } from "react";
import { Award, CheckCircle2, ChevronDown, ChevronRight, Circle, Loader2, Lock } from "lucide-react";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import LoadFailure from "../../components/ui/LoadFailure";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import useAdminLoad from "../../hooks/useAdminLoad";
import { fetchContactPathways } from "../../pathways/adminApi";
import { fmtDaysAgo, fmtInstantDate } from "../pathways/format";
import { kindIcon, kindLabel, levelLabel, statusLabel, typeLabel } from "../pathways/labels";
import { STATUS_PILL, TYPE_BADGE } from "../pathways/statusPill";

const STATE_ICONS = Object.freeze({ completed: CheckCircle2, available: Circle, locked: Lock });
const STATE_TONE = Object.freeze({ completed: "text-emerald-700", available: "text-eh-muted", locked: "text-eh-muted/60" });
/** A practice submission's review status (contract §10.1) -> "admin" key. */
const SUBMISSION_KEYS = Object.freeze({
  pending: "path.crm.submission.pending",
  approved: "path.crm.submission.approved",
  feedback_sent: "path.crm.submission.feedback_sent",
  superseded: "path.crm.submission.superseded",
});

/**
 * The Pathways tab of a CRM contact (spec, "Row click opens their CRM record with a
 * Pathways tab showing full step history"; contract §5.1 `GET /pathways/learners/{email}`):
 * every pathway the contact joined, newest enrolment first, each with its progress, dates
 * and certificate code, and its full step history grouped by level (state, completion
 * date, quiz best score and attempts, the latest practice submission's review status,
 * and a cohort live session's attendance, contract §10.6).
 *
 * `focusPathwayId` (the `?pathway=` of a dashboard row's deep link) opens that pathway's
 * history; the others start folded.
 *
 * @param {object} props
 * @param {string} props.email
 * @param {string|null} props.focusPathwayId
 */
export default function ContactPathways({ email, focusPathwayId }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => { setRows(await fetchContactPathways(email)); }, [email]);
  const { loading, loadError, reload } = useAdminLoad(load);
  const [open, setOpen] = useState(() => new Set(focusPathwayId ? [focusPathwayId] : []));

  if (loading && !rows) return <Loader2 className="w-5 h-5 animate-spin text-eh-muted mt-6" aria-label={t("path.loading")} />;
  if (loadError) {
    return (
      <div className="mt-6">
        <LoadFailure message={tc("errors.loadFailed")} onRetry={reload} retryLabel={t("path.retry")} testId={IDS.crmPathwaysLoadError} />
      </div>
    );
  }
  if (!rows || rows.length === 0) {
    return <p data-testid={IDS.crmPathwaysEmpty} className="mt-6 text-sm text-eh-muted">{t("path.crm.empty")}</p>;
  }

  const toggle = (id) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <ul data-testid={IDS.crmPathways} className="mt-6 space-y-3">
      {rows.map((p) => {
        const expanded = open.has(p.pathway_id);
        return (
          <li
            key={p.pathway_id}
            data-testid={IDS.crmPathway(p.pathway_id)}
            data-open={expanded ? "true" : "false"}
            className="rounded-2xl border border-eh-border-soft"
          >
            <button
              type="button"
              onClick={() => toggle(p.pathway_id)}
              aria-expanded={expanded}
              data-testid={IDS.crmPathwayToggle(p.pathway_id)}
              className="w-full text-left px-4 py-3 flex items-start gap-3"
            >
              {expanded
                ? <ChevronDown className="w-4 h-4 mt-1 text-eh-muted flex-shrink-0" aria-hidden="true" />
                : <ChevronRight className="w-4 h-4 mt-1 text-eh-muted flex-shrink-0" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-eh-ink">{p.title}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${TYPE_BADGE}`}>{typeLabel(t, p.path_type)}</span>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_PILL[p.status] || STATUS_PILL.draft}`}>{statusLabel(t, p.status)}</span>
                </span>
                <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-eh-muted">
                  <span className="tabular-nums">{t("path.crm.percent", { p: p.percent })}</span>
                  <span>{t("path.crm.enrolled", { date: fmtInstantDate(p.enrolled_at, lang) })}</span>
                  <span>{t("path.crm.lastActive", { when: fmtDaysAgo(p.last_activity_at, lang) || "—" })}</span>
                  {p.completed_at && <span>{t("path.crm.completedOn", { date: fmtInstantDate(p.completed_at, lang) })}</span>}
                  {p.certificate_id && (
                    <span className="inline-flex items-center gap-1 text-eh-ink">
                      <Award className="w-3.5 h-3.5" aria-hidden="true" /> {t("path.crm.certificate", { code: p.certificate_id })}
                    </span>
                  )}
                </span>
              </span>
            </button>
            {expanded && <StepHistory pathway={p} lang={lang} />}
          </li>
        );
      })}
    </ul>
  );
}

function StepHistory({ pathway, lang }) {
  const t = useT("admin");
  const levelType = pathway.path_type === "level";
  return (
    <div className="border-t border-eh-border-soft px-4 py-3 space-y-3">
      {pathway.levels.map((level, i) => (
        <section key={level.id} aria-label={levelType ? levelLabel(t, i + 1, level.title) : pathway.title}>
          {levelType && <h4 className="text-xs font-semibold uppercase tracking-wider text-eh-muted mb-1.5">{levelLabel(t, i + 1, level.title)}</h4>}
          <ol className="space-y-1">
            {level.steps.map((s) => {
              const StateIcon = STATE_ICONS[s.state] || Circle;
              const KindIcon = kindIcon(s.kind);
              return (
                <li
                  key={s.id}
                  data-testid={IDS.crmPathwayStep(pathway.pathway_id, s.id)}
                  data-state={s.state}
                  className="flex flex-wrap items-center gap-2 text-sm"
                >
                  <StateIcon className={`w-4 h-4 flex-shrink-0 ${STATE_TONE[s.state] || ""}`} aria-label={t(`path.crm.state.${s.state}`)} />
                  <KindIcon className="w-3.5 h-3.5 text-eh-muted flex-shrink-0" aria-label={kindLabel(t, s.kind)} />
                  <span className="text-eh-ink">{s.title}</span>
                  {!s.required && <span className="text-[11px] text-eh-muted">{t("path.step.optional")}</span>}
                  {s.submission && SUBMISSION_KEYS[s.submission.status] && (
                    <span
                      data-testid={IDS.crmPathwaySubmission(pathway.pathway_id, s.id)}
                      data-status={s.submission.status}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-eh-cream border border-eh-border-soft text-eh-ink"
                    >
                      {t(SUBMISSION_KEYS[s.submission.status], { date: fmtInstantDate(s.submission.submitted_at, lang) })}
                    </span>
                  )}
                  {s.attended && (
                    <span data-testid={IDS.crmPathwayAttended(pathway.pathway_id, s.id)} className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                      {t("path.crm.attended")}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-eh-muted">
                    {s.quiz && s.quiz.attempts > 0 && t(s.quiz.attempts === 1 ? "path.crm.quizOne" : "path.crm.quiz", { best: s.quiz.best_score, n: s.quiz.attempts })}
                    {s.quiz && s.quiz.attempts > 0 && s.completed_at && " · "}
                    {s.completed_at && fmtInstantDate(s.completed_at, lang)}
                  </span>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
}
